// ── CmlLeaderboard — Feature O (feature_cml_leaderboard) ─────────────────────
// Public leaderboard of CML ambassadors per network. Anon-safe RPC; no PII
// beyond the display name they registered with.

import { useEffect, useState } from 'react';
import { fetchCmlLeaderboard } from '../utils/featurePackA';
import { getInitialLang } from '../utils/i18n';
import { supabase } from '../utils/supabase';

const G = {
  bg:    '#0A0A0A',
  card:  '#161616',
  gold:  '#C9A84C',
  white: '#F2E8D0',
  muted: '#888',
  bdr:   'rgba(255,255,255,0.08)',
  serif: "'Cormorant Garamond',Georgia,serif",
  sans:  "'Outfit',system-ui,sans-serif",
};

const NETWORK_LABELS = {
  ca_montreal: 'Montréal',
  fr_paris:    'Paris',
  fr_lorient:  'Lorient',
  fr_lyon:     'Lyon',
  be_brussels: 'Bruxelles',
  ca_toronto:  'Toronto',
};

export default function CmlLeaderboard() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(null);
  const [filter,  setFilter]  = useState('all');
  const lang = getInitialLang();
  const fr   = lang === 'fr';

  useEffect(() => {
    let alive = true;
    (async () => {
      // Check feature flag (anon-safe via app_features RLS allowing read)
      const { data: feat } = await (supabase?.from('app_features').select('enabled').eq('key', 'feature_cml_leaderboard').maybeSingle() || Promise.resolve({ data: null }));
      if (!alive) return;
      if (!feat?.enabled) { setEnabled(false); setLoading(false); return; }
      setEnabled(true);
      const data = await fetchCmlLeaderboard();
      if (alive) { setRows(data); setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const networks = Array.from(new Set(rows.map(r => r.network_id))).filter(Boolean);
  const visible  = filter === 'all' ? rows : rows.filter(r => r.network_id === filter);
  const tone     = (rank) => rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : rank === 3 ? '#CD7F32' : G.gold;

  if (enabled === false) {
    return (
      <div style={{ minHeight: '100vh', background: G.bg, color: G.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: G.sans, padding: 24, textAlign: 'center' }}>
        <div>
          <p style={{ fontFamily: G.serif, fontSize: 22, marginBottom: 8 }}>{fr ? 'Tableau indisponible' : 'Leaderboard unavailable'}</p>
          <p style={{ color: G.muted, fontSize: 13 }}>
            {fr ? "Cette page n'est pas activée." : 'This page is not enabled.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: G.bg, color: G.white, fontFamily: G.sans }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 20px' }}>

        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <p style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: G.gold, margin: 0 }}>
            {fr ? 'Communauté' : 'Community'}
          </p>
          <h1 style={{ fontFamily: G.serif, fontSize: 40, fontWeight: 400, margin: '8px 0 6px', lineHeight: 1.1 }}>
            {fr ? 'Tableau des ambassadeurs ✦' : 'Ambassadors Leaderboard ✦'}
          </h1>
          <p style={{ color: G.muted, fontSize: 13, lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>
            {fr
              ? 'Les CML font tourner BeeLove ville par ville. Ce tableau honore leur travail.'
              : 'CMLs grow BeeLove city by city. This board honours their work.'}
          </p>
        </div>

        {/* Network filter */}
        {networks.length > 1 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
            <button onClick={() => setFilter('all')} style={pillStyle(filter === 'all')}>
              {fr ? 'Toutes les villes' : 'All cities'}
            </button>
            {networks.map(n => (
              <button key={n} onClick={() => setFilter(n)} style={pillStyle(filter === n)}>
                {NETWORK_LABELS[n] || n}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <p style={{ textAlign: 'center', color: G.muted, fontSize: 13 }}>{fr ? 'Chargement…' : 'Loading…'}</p>
        )}

        {!loading && visible.length === 0 && (
          <div style={{ textAlign: 'center', color: G.muted, padding: 40, fontSize: 13 }}>
            {fr ? 'Pas encore de CML actif sur cette ville.' : 'No active CML on this city yet.'}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visible.map((r) => (
            <div key={r.cml_id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 16px', background: G.card,
              border: `1px solid ${r.rank <= 3 ? tone(r.rank) + '55' : G.bdr}`,
              borderRadius: 10,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: tone(r.rank) + '22', color: tone(r.rank),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: G.serif, fontSize: 18, fontWeight: 600, flexShrink: 0,
              }}>
                {r.rank <= 3 ? ['✦', '✧', '·'][r.rank - 1] : r.rank}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: G.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.display_name}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: G.muted }}>
                  {NETWORK_LABELS[r.network_id] || r.network_id}
                </p>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ margin: 0, fontFamily: G.serif, fontSize: 22, color: tone(r.rank), lineHeight: 1 }}>
                  {r.signups_count || 0}
                </p>
                <p style={{ margin: '3px 0 0', fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {fr ? 'inscrits' : 'signups'}
                </p>
              </div>
              {r.events_hosted > 0 && (
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 6 }}>
                  <p style={{ margin: 0, fontFamily: G.serif, fontSize: 22, color: G.muted, lineHeight: 1 }}>
                    {r.events_hosted}
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {fr ? 'événements' : 'events'}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <p style={{ textAlign: 'center', marginTop: 32, fontSize: 11, color: G.muted, lineHeight: 1.7 }}>
          {fr
            ? 'Tu veux ouvrir BeeLove dans ta ville ? '
            : 'Want to open BeeLove in your city? '}
          <a href="#contact" style={{ color: G.gold }}>{fr ? 'Devenir CML →' : 'Become a CML →'}</a>
        </p>
      </div>
    </div>
  );
}

function pillStyle(active) {
  return {
    padding: '6px 14px',
    background: active ? 'rgba(201,168,76,0.14)' : 'transparent',
    border: `1px solid ${active ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.12)'}`,
    color: active ? G.gold : G.muted,
    fontFamily: G.sans, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    borderRadius: 99,
  };
}
